import React from 'react';

/**
 * Catches chunk-load failures caused by stale service workers / cached HTML
 * pointing at removed asset filenames after a deploy. Auto-reloads once so
 * the user doesn't see a broken screen and doesn't have to hit refresh
 * multiple times.
 */
class ChunkErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    const name = String(error && error.name);
    const msg = String(error && (error.message || error));
    const combined = `${name}: ${msg}`;
    const isChunkError =
      /ChunkLoadError/i.test(combined) ||
      /Loading chunk .+ failed/i.test(combined) ||
      /Failed to fetch dynamically imported module/i.test(combined) ||
      /Importing a module script failed/i.test(combined);

    if (isChunkError) {
      const key = 'ayati_chunk_reload_at';
      const now = Date.now();
      const last = Number(sessionStorage.getItem(key) || 0);
      // Only auto-reload once every 30s to avoid loops
      if (now - last > 30000) {
        sessionStorage.setItem(key, String(now));
        window.location.reload();
        return { hasError: false };
      }
    }
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error('App error boundary caught:', error, info);
  }

  handleRetry = () => {
    this.setState({ hasError: false });
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '32px',
          background: '#f8fafc',
          textAlign: 'center',
          fontFamily: 'Inter, system-ui, sans-serif',
        }}>
          <h1 style={{ fontSize: '22px', color: '#0f172a', marginBottom: '8px' }}>
            Something went wrong loading the page
          </h1>
          <p style={{ color: '#6b7280', marginBottom: '24px', maxWidth: '480px' }}>
            This can happen right after we update the site. Please refresh to load the latest version.
          </p>
          <button
            onClick={this.handleRetry}
            data-testid="chunk-error-refresh-button"
            style={{
              padding: '12px 24px',
              background: '#0d9488',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              fontSize: '15px',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Refresh Page
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ChunkErrorBoundary;
